import { findOrg } from '@/api/org-api'
import { organizationKeys } from '@/hooks/organization/query-keys'
import { ensureOrgAccess } from '@/shared/utils/route-utils/org-route-config'
import { useOrgStore } from '@/store/org'
import { getRouteApi, Outlet } from '@tanstack/react-router'
import { useEffect } from 'react'
import type { QueryClient } from '@tanstack/react-query'

export async function createDocumentFlowOrgBeforeLoad({
  params: { orgId },
  abortController,
}: {
  params: { orgId: string }
  abortController: AbortController
}) {
  // Same rule as `/_authenticated/org/$orgId`: a member stays in their own org,
  // a SUPER_ADMIN adopts the org the URL names.
  await ensureOrgAccess(orgId, abortController.signal)
}

export async function createDocumentFlowOrgLoader({
  context: { queryClient },
  params: { orgId },
}: {
  context: { queryClient: QueryClient }
  params: { orgId: string }
}) {
  return await queryClient.ensureQueryData({
    queryKey: organizationKeys.detail(orgId),
    queryFn: () => findOrg(orgId),
  })
}

type DocumentFlowOrgRouteId =
  '/_invoice-flow/org/$orgId' | '/_quote-flow/org/$orgId'

/**
 * Takes the route's ID, not its `Route.useLoaderData`: a route passing its own
 * hook makes its type depend on itself (TS7022), and the only cure inside the
 * route file — a type annotation in `component` — is TS syntax the router's
 * code-splitter cannot parse when it extracts that expression (#2481).
 */
export function createDocumentFlowOrgComponent(
  routeId: DocumentFlowOrgRouteId
) {
  const routeApi = getRouteApi(routeId)

  return function DocumentFlowOrgComponent() {
    const { setOrg } = useOrgStore()
    const org = routeApi.useLoaderData()

    useEffect(() => {
      if (org) {
        setOrg(org)
      }
    }, [org, setOrg])

    return <Outlet />
  }
}
